import config from "@/services/config";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

let recaptchaReadyPromise: Promise<void> | null = null;
const RECAPTCHA_LOAD_TIMEOUT_MS = 10000;
const RECAPTCHA_BYPASS_TOKEN = "local-dev-recaptcha-disabled";

function loadRecaptchaV3Script(siteKey: string): Promise<void> {
  if (recaptchaReadyPromise) {
    return recaptchaReadyPromise;
  }

  recaptchaReadyPromise = new Promise<void>((resolve, reject) => {
    const scriptSrc = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${scriptSrc}"]`,
    );
    const script = existingScript ?? document.createElement("script");
    const timeoutId = window.setTimeout(() => {
      recaptchaReadyPromise = null;
      reject(new Error("reCAPTCHA unavailable. Please try again."));
    }, RECAPTCHA_LOAD_TIMEOUT_MS);

    const resolveWhenReady = () => {
      if (!window.grecaptcha) {
        window.clearTimeout(timeoutId);
        recaptchaReadyPromise = null;
        reject(new Error("reCAPTCHA v3 failed to initialize"));
        return;
      }

      window.grecaptcha.ready(() => {
        window.clearTimeout(timeoutId);
        resolve();
      });
    };

    const handleLoad = () => {
      resolveWhenReady();
    };

    const handleError = () => {
      window.clearTimeout(timeoutId);
      recaptchaReadyPromise = null;
      reject(new Error("Failed to load reCAPTCHA v3"));
    };

    if (existingScript) {
      if (window.grecaptcha) {
        resolveWhenReady();
        return;
      }

      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });
      return;
    }

    script.onload = handleLoad;
    script.onerror = handleError;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });

  return recaptchaReadyPromise;
}

export async function executeRecaptchaV3(action: string): Promise<string> {
  if (config.disableRecaptcha) {
    return `${RECAPTCHA_BYPASS_TOKEN}:${action}`;
  }

  const siteKey = config.recaptchaV3SiteKey;

  if (!siteKey) {
    throw new Error("Missing reCAPTCHA v3 site key");
  }

  await loadRecaptchaV3Script(siteKey);

  const token = await window.grecaptcha?.execute(siteKey, { action });

  if (!token) {
    throw new Error("Failed to generate reCAPTCHA v3 token");
  }

  return token;
}
